import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Group,
  LoadingOverlay,
  Paper,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { usePaymentMethodList } from '../hooks/payment-methods/usePaymentMethodList.ts';
import { IconPlus, IconSearch } from '@tabler/icons-react';
import { modals } from '@mantine/modals';
import { notifySuccess } from '../utils/NotifySuccess.tsx';
import { useQueryClient } from '@tanstack/react-query';
import Fuse from 'fuse.js';
import { PaymentMethod } from '../models/PaymentMethod.ts';
import { DataTable, DataTableColumn } from 'mantine-datatable';
import { notifyError } from '../utils/NotifyError.tsx';
import useRemovePaymentMethod from '../hooks/payment-methods/useRemovePaymentMethod.ts';
import { useDisclosure } from '@mantine/hooks';
import { AddPaymentMethodModal } from '../components/modal/places/AddPaymentMethodModal.tsx';

const ITEM_PER_PAGE = 10;

const fuseOptions = {
  keys: ['name'],
  threshold: 0.4,
  distance: 100,
  ignoreLocation: true,
};

const PaymentMethodListPage: React.FC = () => {
  const { data: paymentMethods, isLoading, error } = usePaymentMethodList();
  const {
    mutateAsync: removePaymentMethod,
    isPending: isRemovePaymentMethodPending,
  } = useRemovePaymentMethod();

  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [
    addPaymentMethodModalOpened,
    { open: openAddPaymentMethodModal, close: closeAddPaymentMethodModal },
  ] = useDisclosure(false);

  const filteredPaymentMethods = useMemo(() => {
    if (!paymentMethods?.length) return [];

    const fuse = new Fuse(paymentMethods, fuseOptions);

    return searchValue
      ? fuse.search(searchValue).map((result) => result.item)
      : paymentMethods;
  }, [paymentMethods, searchValue]);

  const totalItems = filteredPaymentMethods.length;

  const startIndex = (currentPage - 1) * ITEM_PER_PAGE;
  const paginatedPaymentMethods = useMemo(() => {
    return filteredPaymentMethods.slice(startIndex, startIndex + ITEM_PER_PAGE);
  }, [filteredPaymentMethods, startIndex]);

  const columns: DataTableColumn<PaymentMethod>[] = [
    {
      title: '場站付款方式名稱',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'name',
      width: '80%',
      render: ({ name }: { name: string }) => <Text size={'sm'}>{name}</Text>,
    },
    {
      title: '操作',
      titleStyle: { color: 'gray', fontWeight: 'normal' },
      accessor: 'actions',
      width: '20%',
      render: ({ id }: { id: string }) => (
        <Group gap={8}>
          <Button
            color={'red'}
            variant={'outline'}
            size={'xs'}
            onClick={() => {
              modals.openConfirmModal({
                title: (
                  <Text style={{ fontSize: 16, fontWeight: 700 }}>
                    刪除場站付款方式
                  </Text>
                ),
                children: (
                  <Text style={{ fontSize: 14, fontWeight: 400 }}>
                    是否確認刪除場站付款方式？
                  </Text>
                ),
                centered: true,
                labels: { confirm: '確認', cancel: '取消' },
                confirmProps: { color: 'blue' },

                onConfirm: async () => {
                  await handleRemovePaymentMethodClick(id);
                },
              });
            }}
          >
            刪除
          </Button>
        </Group>
      ),
    },
  ];

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchValue(e.currentTarget.value);
      setCurrentPage(1);
    },
    [],
  );

  const handlePaymentMethodCreated = useCallback(async () => {
    closeAddPaymentMethodModal();
    notifySuccess('新增場站付款方式成功');
    setCurrentPage(1);
    await queryClient.invalidateQueries({
      queryKey: ['paymentMethods'],
      exact: true,
    });
  }, [queryClient, closeAddPaymentMethodModal]);

  const handleAddPaymentMethodClick = useCallback(() => {
    openAddPaymentMethodModal();
  }, [openAddPaymentMethodModal]);

  const handleRemovePaymentMethodClick = async (id: string) => {
    if (isRemovePaymentMethodPending) return;

    try {
      await removePaymentMethod(id);
      notifySuccess('刪除場站付款方式成功');

      await queryClient.invalidateQueries({
        queryKey: ['paymentMethods'],
        exact: true,
      });
    } catch (error) {
      notifyError('刪除場站付款方式失敗', error.message);
    }
  };

  if (isLoading) {
    return (
      <>
        <LoadingOverlay
          visible={true}
          overlayProps={{ radius: 'sm', blur: 1 }}
          loaderProps={{
            color: 'rgb(67,154,255)',
            type: 'bars',
            size: 'sm',
          }}
        />
      </>
    );
  }
  if (error) {
    return <div style={{ marginLeft: 'xl' }}>載入可用付款方式時發生錯誤</div>;
  }

  return (
    <>
      {addPaymentMethodModalOpened && (
        <AddPaymentMethodModal
          opened={addPaymentMethodModalOpened}
          onClose={closeAddPaymentMethodModal}
          onSuccess={handlePaymentMethodCreated}
        />
      )}

      <Paper shadow="xl" radius="lg">
        <Group
          justify="space-between"
          p="24px 24px 20px 24px"
          style={{ borderBottom: 'none', flexShrink: 0 }}
        >
          <Title order={2}>場站付款方式管理</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            size={'md'}
            variant="filled"
            onClick={handleAddPaymentMethodClick}
          >
            新增場站付款方式
          </Button>
        </Group>

        <div style={{ flexShrink: 0, paddingInline: 24, paddingBottom: 10 }}>
          <TextInput
            placeholder="搜尋場站付款方式名稱"
            leftSection={<IconSearch size={16} />}
            value={searchValue}
            onChange={handleSearchChange}
            style={{ width: 321, height: 40 }}
          />
        </div>

        <Box style={{ paddingInline: 24, paddingBottom: 24 }}>
          <DataTable
            idAccessor={'id'}
            highlightOnHover
            height={600}
            columns={columns}
            records={paginatedPaymentMethods}
            recordsPerPage={ITEM_PER_PAGE}
            page={currentPage}
            onPageChange={setCurrentPage}
            totalRecords={totalItems}
            verticalSpacing={'xs'}
            noRecordsText={'尚無場站付款方式'}
            paginationText={({ from, to, totalRecords }) =>
              `顯示 ${from} - ${to} 筆場站付款方式，共 ${totalRecords} 筆`
            }
            styles={{
              pagination: {
                color: 'gray',
              },
            }}
          />
        </Box>
      </Paper>
    </>
  );
};

export default PaymentMethodListPage;
